import { useContext, useEffect, useState } from "react";
import Footer from "./Footer";
import Header from "./Header";
import { getDownloadURL, ref } from "@firebase/storage";
import { storage } from "../firebase";
import { HeroImageWithSkeleton } from "./ImageSkeleton";
import LanguageContext from "./contexts/languageContext";
import { Helmet } from "react-helmet-async";

interface Translations {
  en: {
    biography: string[];
    soloExhibitions: string;
    groupExhibitions: string;
    awardsAndHonors: string;
    degrees: string;
  };
  ko: {
    biography: string[];
    soloExhibitions: string;
    groupExhibitions: string;
    awardsAndHonors: string;
    degrees: string;
  };
}

interface Award {
  year: number;
  events: {
    en: string;
    ko: string;
  }[];
}

interface Exhibition {
  year: number;
  events: {
    en: string;
    ko: string;
  }[];
}

interface Degrees {
  year: number;
  events: {
    en: string;
    ko: string;
  }[];
}

const translations: Translations = {
  en: {
    biography: [
      `She was encouraged by her brother to study art because of her talent for drawing and manual dexterity. She majored in ceramics at Kookmin University and worked in fashion relations after graduation.
      Over time, she lost interest in fashion, and after a long break from that job, she recalled one of the happiest times of her life: her college years. Staying up all night to finish a project, sitting on a campus bench at dawn with a cup of vending machine coffee, and watching the sun rise filled her with a sense of accomplishment, pride, joy, and happiness, forgetting the fatigue in her body. That cherished memory gave her the courage to return to the world of pure creation once again, albeit belatedly.`,
      `She began her journey into the world of painting, something she had vaguely longed for since childhood. Initially a hobby and a self-taught pursuit, her interest grew, leading her to formally start studying painting. She moved to Japan to study it in earnest, completing graduate school at Nagoya University of the Arts.
Still feeling that her skills were immature and unfinished, she furthered her studies by enrolling as a research student for two years at Aichi Prefectural University of the Arts. It was during this time that the concept of "Present Continuous" was born, marking the start of her unique artistic world.
Always in search of something new and unprecedented, she found herself confronted with the limits of art. The existing works and concepts of the art world felt more like distractions than inspirations.`,
      `She consciously distanced herself from galleries, books, and external influences, choosing instead to focus on observing nature. Through this process, she discovered her surroundings and her artistic strengths. Her recurring theme was people, particularly the relationships between individuals—social, political, and national.
Her storytelling approach integrated dots, lines, planes, diverse coloring techniques, and varied textures. She drew from the color and light of ceramics, the surfaces of pottery, and her experiences in the fashion industry, merging these influences onto her canvas.
It took considerable time for her to let go of rigid, conventional perspectives on art. Even now, remnants of those perspectives occasionally stir within her. `,
      `While working on the "Present Continuous" series, she explored new ideas through techniques like "zooming in and out" and "filling in gaps." These methods led her to create compositions blending sketches with painting, ultimately building her unique artistic world. `,
      `Her Tokyo exhibition marked a significant milestone. The gallery was a hub for art practitioners, experts, critics, and professors, making it an ideal space for a new artist. Presenting the "Present Continuous" series for the first time, her work was praised for being unseen, unique, and distinctive.

One notable element was her use of purple—a color rarely employed in Japanese art—which intrigued many viewers. She later exhibited at the Museum of Art in Gifu Prefecture, a unique venue where artists are invited based on rigorous evaluations by experts from institutions such as the Toyota Museum of Art and Nagoya City Art Academy. In 2019, she received the highest score and held a solo exhibition there, which garnered high praise and marked a meaningful accomplishment.

As the Japanese art industry continued to favor conventional styles, she felt a growing sense of despair, though not hopelessness. Coupled with the challenges of the pandemic, she decided to return to Korea before it was too late.

Her belief that the eyes of visitors—professional or non-professional, regardless of nationality—are fundamentally similar gave her confidence. Now, with unique techniques and her own artistic vision, she is both excited and nervous to showcase her work in Korea, where she feels like she is just beginning. She hopes to share her creations with many visitors.`,
    ],
    soloExhibitions: "SOLO EXHIBITIONS",
    groupExhibitions: "GROUP EXHIBITIONS",
    awardsAndHonors: "AWARDS AND HONORS",
    degrees: "DEGREES",
  },
  ko: {
    biography: [
      `그녀는 어려서부터 그림이나 손재주가 뛰어났다. 오빠의 권유로 미술대를 선택하게 되었고, 국민대학교 조형대학에서 도자공예를 전공한 뒤 졸업 후 패션 관련 일을 하게 되었다. 그러나 패션 일을 하며 시간이 갈수록 흥미를 잃어갔고, 결국 일을 그만둔 뒤 꽤 긴 휴식기를 가졌다. 그 기간 동안 그녀는 인생에서 가장 행복했던 기억을 떠올렸다. 그것은 대학 시절 밤새 작업을 마친 뒤 새벽에 자판기 커피 한 잔을 들고 교정의 벤치에 앉아 떠오르는 해를 바라보며 느꼈던 성취감과 뿌듯함, 그리고 행복감이었다. 이 소중한 기억의 힘으로 그녀는 비록 늦었지만 다시 순수 창작의 세계로 돌아갈 용기를 낼 수 있었다.`,
      `그녀가 시작한 것은 회화였다. 어릴 때부터 막연히 동경했던 대상이기도 했다. 처음에는 취미로 독학하며 시작했지만, 정식으로 배워보고 싶다는 열망에 일본으로 건너갔다. 나고야 예술대학교 대학원을 수료한 뒤, 아직 미숙하고 완성되지 않은 부분을 깊이 연구하기 위해 아이치 현립 예술대학교 미술학부 연구생 과정 2년을 거쳤다. 이 시기를 통해 그녀는 현재 진행형인 나만의 예술 세계를 구축하게 되었다.`,
      `그녀는 늘 새로운 것을 찾고 남들이 하지 않은 것을 추구했지만, 그런 대상은 쉽게 보이지 않았다. 이미 예술의 종말론까지 논의되는 상황 속에서 기존의 예술 작품과 개념들이 오히려 그녀에게 방해가 되었다. 그래서 갤러리나 책을 찾지 않고, 모든 것과 단절한 채 자연을 바라보기 시작했다. 이 과정에서 그녀는 자신의 주변과 장점을 발견했고, 작업의 테마로 언제나 '사람'을 중심에 두었다. 사람과 사람 사이에서 발생하는 인간의 관계성을 다뤘으며, 사회적, 정치적, 국가적 관계를 포함해 그것들을 어떻게 표현할지를 스토리 형식으로 풀어냈다.`,
      `점과 선, 면, 다양한 채색과 질감 표현 기법을 통해 도자기의 색과 빛, 표면, 그리고 패션 활동의 경험이 모두 화폭에 담겼다. 고정적이고 정형화된 미술 관점을 버리기까지는 꽤 오랜 시간이 걸렸지만, 여전히 그 잔재는 그녀 안에서 꿈틀대고 있다.`,
      `‘현재진행형’ 연작을 작업하면서 그녀는 확대와 축소, 채움과 여백 구성 등 새로운 아이디어를 연구하고, 회화를 접목해 밑그림 위에 작업하며 나만의 세계를 구축했다. 도쿄의 갤러리에서 열린 첫 전시에서 그녀는 ‘현재진행형’을 발표했고, 많은 전문가에게 호평을 받았다. 그들은 한결같이 그녀의 작품이 독보적이고 독특하며 기존에 본 적 없는 새로운 시도라고 평가했다. 특히 다양한 채색, 그리고 일본에서는 잘 사용하지 않는 보라색 계열의 색상을 많이 사용한 점이 전문가들에게 신선하고 낯설게 느껴졌던 것 같다.`,
      `기후현의 극소미술관은 일본에서 보기 드문 독특한 미술관으로, 그룹 전시에 추천받아 참가한 그녀는 가장 높은 평가를 받아 개인전을 열게 되었다. 토요타 미술관, 나고야시 미술관 관장, 평론가 등 각계 전문가 10~15명이 심사에 참여한 가운데, 그녀는 2019년에 가장 높은 점수를 받고 전시를 열었다. 이 전시는 좋은 평가와 함께 뜻깊은 성과를 남겼다.`,
      `일본에서 활발히 활동하던 그녀는 구상 중심의 일본 미술업계 분위기와 전문가들의 조언 속에서 절망 아닌 절망을 느꼈다. 여기에 코로나로 활동이 중단되면서 더 늦기 전에 한국으로 돌아가기로 결심했다. 그녀는 전문가든 비전문가든, 국적을 불문하고 관람객의 눈은 어디서든 비슷하다는 사실을 경험으로 느꼈다.`,
      `이제 그녀는 독특한 기법으로 나만의 세계를 만들어 한국에 선보일 준비를 하고 있다. 설레는 동시에 현실적인 두려움도 느껴지지만, 그녀는 이제 한국에서 새로운 시작을 준비하고 있다. 많은 관람객들에게 자신의 작품을 보여주고 싶다는 열망을 품고 있다.`,
    ],
    soloExhibitions: "개인전",
    groupExhibitions: "그룹전",
    awardsAndHonors: "수상 경력",
    degrees: "학력",
  },
};

const awards: Award[] = [
  {
    year: 2018,
    events: [
      {
        en: "1st Mumbai Biennale - Sir JJ School of Art, Mumbai",
        ko: "제1회 뭄바이 비엔날레 - 뭄바이 Sir JJ School of Art, 뭄바이",
      },
      {
        en: "Modern Art Winners Exhibition - Gallery Hinoki, Tokyo",
        ko: "모던아트 수상작가전 - 갤러리 히노끼, 도쿄",
      },
      {
        en: "68th Modern Art Exhibition (Newcomers) - Tokyo Metropolitan Museum of Art, Tokyo",
        ko: "제68회 모던아트전(신인상) - 도쿄도 미술관, 도쿄",
      },
    ],
  },
  {
    year: 2017,
    events: [
      {
        en: "Modern Art Chubu Artists Exhibition (Junichi Prize, Chubu Artists Prize) - AICHI Prefectural Museum of Art, Nagoya",
        ko: "모던아트 중부 작가전(주니치상, 중부작가상) - 아이치현 미술관, 나고야",
      },
      {
        en: "CHENNAI CHAMBER Biennale - INDIA NATIONAL LALITKALA AKDEMI, Chennai",
        ko: "첸나이 챔버 비엔날레 - 인도 국립 라리타카 아카데미, 첸나이",
      },
      {
        en: "67th Modern Art Exhibition (Newcomers) - Tokyo Metropolitan Museum of Art, Tokyo",
        ko: "제67회 모던아트전 초입선 - 도쿄도 미술관, 도쿄",
      },
    ],
  },
  {
    year: 2007,
    events: [
      {
        en: "The 92nd Tokyo Two-Discipline Exhibition Painting Division First Prize - New National Art Museum, Tokyo",
        ko: "제92회 동경 이과전 회화부 초입선 - 신국립 미술관, 도쿄",
      },
    ],
  },
  {
    year: 2006,
    events: [
      {
        en: "56th Chubu Two-Discipline Exhibition Painting Division First Prize (57th Shimamodo Award, 61st Special Prize) - AICHI Prefectural Museum of Art, Nagoya",
        ko: "제56회 중부 이과전 회화부 초입선(57회 시마모도상, 61회 특선) - 아이치현 미술관, 나고야",
      },
    ],
  },
];

const groupExhibitions: Exhibition[] = [
  {
    year: 2024,
    events: [
      {
        en: "50th Anniversary Territory Exhibition - Maru Art Center, Seoul",
        ko: "제50주년 영토전 - 마루 아트센터, 서울",
      },
    ],
  },
  {
    year: 2021,
    events: [
      {
        en: "How to Write in Contemporary Art 2021 - Gakso Museum of Art, Gifu",
        ko: "현대미술의 작법 2021 - 극소미술관, 기후",
      },
    ],
  },
  {
    year: 2020,
    events: [
      {
        en: "Messages of the 21st Century - Japan's Oldest Stone Museum, Gifu",
        ko: "21세기 Message전 - 日本最古の石博物館, 기후",
      },
    ],
  },
  {
    year: 2019,
    events: [
      {
        en: "CAF.N Exhibition - Saitama Prefectural Museum of Modern Art, Saitama",
        ko: "CAF.N전 - 사이타마현립 근대미술관, 사이타마",
      },
      {
        en: "Contemporary Art Perspectives 20219 - Gokso Museum of Art, Gifu",
        ko: "현대미술의 시점 2019 - 극소미술관, 기후",
      },
    ],
  },
  {
    year: 2018,
    events: [
      {
        en: "6th Contemporary Art from the Point of ZERO - Gallery Hinoki, Tokyo",
        ko: "제6회 현대미술 ZERO의 시점 - 갤러리 히노끼, 도쿄",
      },
      {
        en: "68th Modern Art Exhibition - Kyoto City Museum of Art Annex, Kyoto",
        ko: "제68회 모던아트전 - 교토시 미술관 별관, 교토",
      },
    ],
  },
  {
    year: 2017,
    events: [
      {
        en: "CAF.N GANAZAWA Exhibition - GANAZAWA Museum of 21st Century Art, Ganazawa",
        ko: "CAF.N GANAZAWA전 - 가나자와 21세기 미술관, 가나자와",
      },
    ],
  },
  {
    year: 2015,
    events: [
      {
        en: "Japan-Korea Artist Exchange Exhibition - Nagoya Civic Gallery, Nagoya",
        ko: "한일작가교류전 - 나고야 시민갤러리, 나고야",
      },
      {
        en: "The Spread of Thought Exhibition - Sky World Asahi, Owariasai",
        ko: "사고의 확산전 - 스카이월드아사이, 오와리아사이",
      },
    ],
  },
  {
    year: 2011,
    events: [
      {
        en: "MEISOU Exhibition, Matsuzakaya Department Store South Building, Nagoya",
        ko: "메이소우전 - 마쓰자카야 백화점 남관, 나고야",
      },
      {
        en: "Art Mira 21 <Power of Art> - AICHI Prefectural Museum of Art, Nagoya",
        ko: "Art Mira 21 <Power of Art> - 아이치현 미술관, 나고야",
      },
    ],
  },
];

const soloExhibitions: Exhibition[] = [
  {
    year: 2024,
    events: [
      {
        en: "Gallery Alley Homecoming Invitational Exhibition - Sinsa-dong, Seoul",
        ko: "갤러리 앨리 귀국기념 초대전 - 서울, 신사동",
      },
    ],
  },
  {
    year: 2019,
    events: [
      {
        en: "Gokso Museum of Art, Gifu",
        ko: "극소미술관 - 기후",
      },
    ],
  },
  {
    year: 2018,
    events: [
      {
        en: "17th Busan International Art Fair Invited Artist - Bexco, Busan",
        ko: "2018 제17회 부산국제아트페어 초대작가 - 벡스코, 부산",
      },
    ],
  },
  {
    year: 2017,
    events: [
      {
        en: "16th Busan International Art Fair Invited Artist - Bexco, Busan",
        ko: "2017 제16회 부산국제아트페어 초대작가 - 벡스코, 부산",
      },
      {
        en: "Gallery MEHODO - Nagoya",
        ko: "MEHODO 갤러리, 나고야",
      },
      {
        en: "ART SPACE Compass - Kobashi, Tokyo",
        ko: "ART SPACE 나침반 - 교바시, 도쿄",
      },
      {
        en: "Gifu MEDIA Cosmos Everyone's Gallery-2 - Gifu",
        ko: "기후 미디어 코스모스 모두의 갤러리 - 기후",
      },
    ],
  },
  {
    year: 2016,
    events: [
      {
        en: "ART SPACE Compass - Kobashi, Tokyo",
        ko: "ART SPACE 나침반 - 교바시, 도쿄",
      },
    ],
  },
  {
    year: 2015,
    events: [
      {
        en: "Gallery MEHODO - Nagoya",
        ko: "MEHODO 갤러리, 나고야",
      },
    ],
  },
  {
    year: 2012,
    events: [
      {
        en: "Gallery MEHODO - Nagoya",
        ko: "MEHODO 갤러리, 나고야",
      },
    ],
  },
];

const degree: Degrees[] = [
  {
    year: 2014,
    events: [
      {
        en: "2 years as a research student at Aichi Prefectural University of the Arts, Department of Fine Arts",
        ko: "아이치현립 예술대학 미술학과 연구생 2년",
      },
    ],
  },
  {
    year: 2011,
    events: [
      {
        en: "Graduate School of Fine Arts, Nagoya University of the Arts, Painting Research Area",
        ko: "나고야 예술대학 대학원 미술연구과 회화연구영역 수료(수사학위)",
      },
    ],
  },
  {
    year: 1986,
    events: [
      {
        en: "Graduated from Kookmin University, College of Fine Arts, Department of Ceramics, majoring in Life Arts",
        ko: "국민대학교 조형대학 생활미술학과 도자공예 전공 졸업(미술학사)",
      },
    ],
  },
];

const getFontFamily = (language: "en" | "ko") => {
  return language === "ko"
    ? '"pretendard", sans-serif'
    : '"neue-haas-grotesk-display", sans-serif';
};

const BiographyPage = () => {
  const [heroImageUrl, setHeroImageUrl] = useState("");
  const context = useContext(LanguageContext);

  if (!context) {
    throw new Error("BiographyPage must be used within a LanguageProvider");
  }

  const { language } = context;
  const content = translations[language];

  const textStyle = {
    fontFamily: getFontFamily(language),
    fontWeight: language === "ko" ? 600 : 800,
    fontStyle: "normal" as const,
  };

  const titleStyle = {
    fontFamily: getFontFamily(language),
    fontWeight: 800,
    fontStyle: "normal" as const,
  };

  const renderSection = (
    title: string,
    items: (Award | Exhibition | Degrees)[]
  ) => (
    <div>
      <h1
        className="text-4xl sm:text-6xl font-bold mb-12 sm:mb-20"
        style={titleStyle}
      >
        {title}
      </h1>

      <div className="space-y-12">
        {Array.from({ length: Math.ceil(items.length / 5) }).map((_, index) => {
          const rowItems = items.slice(index * 5, (index + 1) * 5);

          return (
            <div key={index}>
              {index === 0 ? (
                <div className="w-full h-px bg-black mb-8 md:mb-6" />
              ) : (
                <div className="w-full h-px bg-black my-8 md:my-6" />
              )}
              <div className="grid grid-cols-1 md:grid-cols-5 gap-8">
                {rowItems.map((item) => (
                  <div key={item.year} className="space-y-4">
                    <h2 className="text-xl md:text-2xl font-bold mb-4 md:mb-6">
                      {item.year}
                    </h2>
                    <div className="space-y-4">
                      {item.events.map((event, eventIndex) => (
                        <p
                          key={eventIndex}
                          className="text-sm leading-relaxed"
                          style={textStyle}
                        >
                          {language === "ko" ? event.ko : event.en}
                        </p>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  useEffect(() => {
    const loadImages = async () => {
      try {
        const heroRef = ref(storage, "artist/artist.webp");
        const heroUrl = await getDownloadURL(heroRef);
        setHeroImageUrl(heroUrl);
      } catch (error) {
        console.error("Error loading images:", error);
      }
    };
    loadImages();
  }, []);

  return (
    <>
      <Helmet>
        <title>바이오그래피 | Biography</title>
        <meta
          name="description"
          content="이선희 작가의 생애와 작품에 대한 관념 및 철학을 설명하는 페이지입니다."
        />
      </Helmet>
      <div className="relative min-h-screen bg-white text-gray-800 md:pt-32">
        <Header />
        <main className="md:pt-4">
          <div className="flex flex-col md:flex-row max-w-8xl mx-auto sm:px-6 md:px-6 lg:px-12 xl:px-24">
            <div className="md:w-3/12 md:sticky md:top-24 md:h-[calc(100vh-6rem)] flex-shrink-0">
              <div className="w-full h-full">
                <HeroImageWithSkeleton
                  imageUrl={heroImageUrl}
                  alt="artist"
                  className="w-full h-auto object-contain"
                />
              </div>
            </div>
            <div className="md:w-9/12 md:pl-12 pt-8 md:pt-0">
              {content.biography.map((paragraph, index) => (
                <p
                  key={index}
                  className="px-4 md:px-0 md:text-2xl lg:text-3xl leading-relaxed font-display text-gray-300 mb-8"
                  style={textStyle}
                >
                  {paragraph}
                </p>
              ))}
            </div>
          </div>
          <div className="mx-auto px-6 sm:px-6 mb-10 mt-20">
            {renderSection(content.soloExhibitions, soloExhibitions)}
            <div className="my-20" />
            {renderSection(content.groupExhibitions, groupExhibitions)}
            <div className="my-20" />
            {renderSection(content.awardsAndHonors, awards)}
            <div className="my-20" />
            {renderSection(content.degrees, degree)}
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
};

export default BiographyPage;
